// React and Router imports
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Third-party library imports
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import Select from "react-select";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";
// Firebase imports
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import { useAutoSnapshot } from "../../utils/snapshotUtils";
import useUnsavedChangesGuard from "../../utils/useUnsavedChangesGuard";
import useTemplatePrefill, { readLastParentForTemplate, rememberParentForTemplate } from "../../utils/useTemplatePrefill";
import useTemplateSnippets, { insertTemplateToolbarHandler, decorateInsertTemplateButton } from "../../utils/useTemplateSnippets";
import { editorHasContent } from "../../utils/contentTransforms";
import { auth } from "../../firebase.js";

// Component imports
import Sidebar from "../shared/Sidebar.js";
import CommandCenterFilePickerModal from '../shared/CommandCenterFilePickerModal';

// Utility imports
import { debounce } from "../../utils/debounceUtils.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";

// Styles imports
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/Create.css";
import "../../css/Account.css";
import "../../css/EditDocument.css";
import "../../css/DocumentationMainPage.css";
import "../../css/DocumentationHomeComponent.css"
import "../../css/HomePage.css"
import "../../css/QuillOverrides.css";
import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";
import { notifyBlockedShareRecipients, readShareResponse } from "../../utils/sharePermissions";


// PDF Worker Configuration
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
// Quill Core Imports
const Inline = Quill.import("blots/inline");
const Block = Quill.import("blots/block");

const FullscreenModule = function (quill, options) {
  const container = quill.container;
  let isFullscreen = false;

  // Create button element to add to toolbar
  this.toggleFullscreen = function () {
    isFullscreen = !isFullscreen;

    if (isFullscreen) {
      container
        .closest(".EditDocument__quill-container")
        .classList.add("fullscreen-mode");
      document.body.style.overflow = "hidden";
    } else {
      container
        .closest(".EditDocument__quill-container")
        .classList.remove("fullscreen-mode");
      document.body.style.overflow = "";
    }

    // Trigger a window resize event to ensure the editor adjusts its layout
    window.dispatchEvent(new Event("resize"));
  };
};

// Custom Inline Code Block
class InlineCode extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }

  static formats(node) {
    return true;
  }
}

InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

// Custom Multi-line Blockquote
class MultiLineBlockquote extends Block {
  static create() {
    const node = super.create();
    node.setAttribute("class", "multi-line-blockquote");
    return node;
  }
  static formats(node) {
    return true;
  }
}

MultiLineBlockquote.blotName = "multi-line-blockquote";
MultiLineBlockquote.tagName = "multi-line-blockquote";
MultiLineBlockquote.className = "multi-line-blockquote";

// Register Custom Quill Components
Quill.register(MultiLineBlockquote);
Quill.register(InlineCode);

function Create() {
  // Document type and status states
  const [selectedType, setSelectedType] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showTableOfContents, setShowTableOfContents] = useState(true); // BBD-58 — spaces only
  const [hasSpaces, setHasSpaces] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { enhancedRightClickEnabled, snapshotFrequencyMinutes } = useQuickLinks();
  const quillRef = useRef(null);
  const { taskRefProps } = useTaskReference(quillRef);

  // Content and metadata states
  const [content, setContent] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Document relationship states
  const [allDocumentation, setAllDocumentation] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState("");
  const [selectedParent, setSelectedParent] = useState("");

  // User and authentication states
  const [uid, setUid] = useState("");

  // Document templates (BBD-106/107/108): gallery → fields → server render →
  // prefill this form; kits are created server-side and we navigate away.
  const appliedTemplateRef = useRef(null);
  const templatePrefill = useTemplatePrefill({
    authorUid: uid,
    hasContent: () => Boolean(name.trim() || description.trim()) || editorHasContent(content),
    apply: (rendered, template) => {
      appliedTemplateRef.current = template.ref;
      if (rendered.doc_type) setSelectedType(rendered.doc_type);
      setName(rendered.name || "");
      setDescription(rendered.description || "");
      setContent(rendered.content || "");
      if (rendered.doc_type === "Page") {
        const remembered = readLastParentForTemplate(template.ref);
        if (remembered && allDocumentation.some((doc) => doc.label === remembered)) {
          setSelectedParent(remembered);
        }
      }
    },
  });
  const templateSnippets = useTemplateSnippets(quillRef);
  const [userEmail, setUserEmail] = useState("");

  // Collaboration states
  const [emails, setEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");

  // Social media and custom link states
  const [customLink, setCustomLink] = useState("");
  const [customLinkTitle, setCustomLinkTitle] = useState("");
  const [discordLink, setDiscordLink] = useState("");
  const [linkedInLink, setLinkedInLink] = useState("");
  const [youtubeLink, setYoutubeLink] = useState("");
  const [githubLink, setGithubLink] = useState("");
  const [twitterLink, setTwitterLink] = useState("");
  const [blueskyLink, setBlueskyLink] = useState("");

  // Split view state
  const [showSplitView, setShowSplitView] = useState(window.innerWidth > 1200);
  const [selectedViewDoc, setSelectedViewDoc] = useState(null);
  const [selectedViewDocName, setSelectedViewDocName] = useState("");
  const [viewDocContent, setViewDocContent] = useState("");
  const [viewDocTitle, setViewDocTitle] = useState("");

  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [attachmentDescription, setAttachmentDescription] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState(null);
  const [snapshotSaveStatus, setSnapshotSaveStatus] = useState(null); // 'saving', 'success', 'error', null

  // Duplicate detection states
  const [duplicateSuggestions, setDuplicateSuggestions] = useState([]);
  const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
  const [duplicateCheckError, setDuplicateCheckError] = useState(null);
  const [showDuplicatesSuggestions, setShowDuplicatesSuggestions] = useState(false);

  const navigate = useNavigate();

  // Auto-snapshot functionality - defined after all state variables
  const documentData = {
    type: selectedType.toLowerCase(),
    name: name,
    content: content,
    description: description,
    parentPage: selectedType === 'Page' ? selectedParent : undefined,
    isPublic: isPublic,
    completed: completed
  };
  const { startAutoSnapshot, stopAutoSnapshot, saveManualSnapshot } = useAutoSnapshot(
    documentData, 
    snapshotFrequencyMinutes, 
    !!uid && !!name && !!content && selectedType && name.trim() !== '' && content.trim() !== ''
  );

  // Authentication Effect
  useEffect(() => {
    setLoadingMessage('Checking authentication...');
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
        setUserEmail(user.email);
      } else {
        setUid(null);
        setUserEmail(null);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isFullscreen]);

  // Auto-snapshot effect - start/stop based on document state
  useEffect(() => {
    if (uid && name && content && selectedType) {
      startAutoSnapshot();
    } else {
      stopAutoSnapshot();
    }

    // Cleanup on unmount
    return () => {
      stopAutoSnapshot();
    };
  }, [uid, name, content, selectedType, snapshotFrequencyMinutes, startAutoSnapshot, stopAutoSnapshot]);

  // Duplicate detection effect - check for similar documents
  const checkForDuplicates = async (title, description, type) => {
    // Minimum character requirements
    const MIN_TITLE_LENGTH = 3;
    const MIN_DESCRIPTION_LENGTH = 5;

    if (!type || !title || title.trim().length < MIN_TITLE_LENGTH) {
      setDuplicateSuggestions([]);
      setShowDuplicatesSuggestions(false);
      return;
    }

    // Only check if description meets minimum length OR is empty (optional)
    if (description && description.trim().length < MIN_DESCRIPTION_LENGTH) {
      return; // Don't check yet if description is being typed but incomplete
    }

    setDuplicateCheckLoading(true);
    setDuplicateCheckError(null);

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      if (!token) {
        console.error("User is not authenticated.");
        setDuplicateCheckLoading(false);
        return;
      }

      const requestBody = {
        document_type: type.toLowerCase(),
        title: title.trim(),
        ...(description && description.trim().length >= MIN_DESCRIPTION_LENGTH && { description: description.trim() }),
      };

      const response = await fetch(
        "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/analyze-duplicates/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `${token}`,
            "auth-token": `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.potential_duplicates && data.potential_duplicates.length > 0) {
          setDuplicateSuggestions(data.potential_duplicates);
          setShowDuplicatesSuggestions(true);
        } else {
          setDuplicateSuggestions([]);
          setShowDuplicatesSuggestions(false);
        }
      } else {
        console.error("Failed to check for duplicates:", response.status);
        setDuplicateSuggestions([]);
        setShowDuplicatesSuggestions(false);
      }
    } catch (error) {
      console.error("Error checking for duplicates:", error);
      setDuplicateCheckError("Unable to check for similar documents");
    } finally {
      setDuplicateCheckLoading(false);
    }
  };

  // Debounced duplicate check
  const debouncedCheckForDuplicates = debounce(checkForDuplicates, 800);

  // Effect to trigger duplicate check when name, description, or selectedType changes
  useEffect(() => {
    if (uid && selectedType && name) {
      debouncedCheckForDuplicates(name, description, selectedType);
    } else {
      setDuplicateSuggestions([]);
      setShowDuplicatesSuggestions(false);
    }
  }, [name, description, selectedType, uid]);

  // Unsaved-changes guard (BBD-56): anything typed into the form counts as
  // unsaved work until the document is successfully created.
  const hasUnsavedChanges =
    Boolean(name.trim() || description.trim() || attachmentFile) ||
    editorHasContent(content);
  const { releaseGuard } = useUnsavedChangesGuard(hasUnsavedChanges);

  const handleSubmit = async () => {
    // Validation checks
    if (!selectedType) {
      alert("Please select a content type (Space or Page).");
      return;
    }

    if (!name || name.trim() === "") {
      alert("Please enter a name for your document.");
      return;
    }

    // Check that name only contains letters and numbers
    if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
      alert(
        "Document name must contain only letters, numbers, spaces, periods, dashes, and underscores."
      );
      return;
    }

    // Check for duplicate document name
    const nameExists = allDocumentation.some(doc => (doc.label || "").toLowerCase() === name.trim().toLowerCase());
    if (nameExists) {
      alert("A document with this name already exists. Please choose a different name.");
      return;
    }

    if (selectedType === "Page" && !selectedParent) {
      alert("Please select a parent for the page.");
      return;
    }

    // Set loading to true immediately after validation passes
    setIsLoading(true);
    setLoadingMessage('Preparing document creation...');

    try {
      if (emails.length > 0) {
        setLoadingMessage('Setting up permissions...');
        await createPermissionTableEntry();
      }

      setLoadingMessage('Creating document...');

      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;

      if (!token) {
        throw new Error("User is not authenticated.");
      }

      let response;

      // If there's an attachment file, use FormData
      if (attachmentFile) {
        const formData = new FormData();
        
        // Add all the document data
        formData.append('type', selectedType);
        formData.append('content', content);
        formData.append('name', name);
        formData.append('isPublic', isPublic ? 'true' : 'false');
        formData.append('completed', completed ? 'true' : 'false');
        formData.append('showTableOfContents', showTableOfContents ? 'true' : 'false');
        formData.append('author', uid);
        formData.append('description', description);
        formData.append('space', selectedSpace);
        formData.append('parentPage', selectedParent);
        formData.append('customLink', customLink);
        formData.append('customLinkTitle', customLinkTitle);
        formData.append('twitterLink', twitterLink);
        formData.append('blueskyLink', blueskyLink);
        formData.append('discordLink', discordLink);
        formData.append('linkedInLink', linkedInLink);
        formData.append('githubLink', githubLink);
        formData.append('youtubeLink', youtubeLink);
        
        // Add emails as JSON string
        formData.append('emails', JSON.stringify(emails));
        
        // Add attachment file and description
        formData.append('attachment', attachmentFile);
        if (attachmentDescription) {
          formData.append('attachmentDescription', attachmentDescription);
        }

        response = await fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/create/",
          {
            method: "POST",
            headers: {
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
              // Don't set Content-Type for FormData, let browser set it with boundary
            },
            body: formData,
          }
        );
      } else {
        // No attachment, use JSON as before
        const data = {
          type: selectedType,
          isPublic: isPublic,
          emails: emails,
          content: content,
          name: name,
          author: uid,
          completed: completed,
          showTableOfContents: showTableOfContents,
          description: description,
          space: selectedSpace,
          parentPage: selectedParent,
          customLink: customLink,
          customLinkTitle: customLinkTitle,
          twitterLink: twitterLink,
          blueskyLink: blueskyLink,
          discordLink: discordLink,
          linkedInLink: linkedInLink,
          githubLink: githubLink,
          youtubeLink: youtubeLink,
        };

        response = await fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/documentation/create/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            },
            body: JSON.stringify(data),
          }
        );
      }

      if (response.ok) {
        setLoadingMessage('Document created successfully! Redirecting...');
        if (appliedTemplateRef.current && selectedType === "Page" && selectedParent) {
          rememberParentForTemplate(appliedTemplateRef.current, selectedParent);
        }
        releaseGuard();
        navigate(`/documentation/${selectedType.toLowerCase()}/${name}/${uid}/`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit the form.");
      }
    } catch (error) {
      console.error("Error submitting the form:", error);
      alert(error.message || "An error occurred while creating the document.");
      setIsLoading(false); // Stop loading on error
    }
  };

  const loggedOutOptions = [
    {
      label: "Login",
      icon: <i className="fas fa-sign-in-alt"></i>,
      url: "/login",
      onClick: () => navigate("/login"),
    },
    {
      label: "Register",
      icon: <i className="fas fa-user-plus"></i>,
      url: "/register",
      onClick: () => navigate("/register"),
    },
    {
      label: "Try My Notebook",
      icon: <i className="fas fa-book"></i>,
      url: "/my-notebook",
      onClick: () => navigate("/my-notebook"),
    },
    {
      label: "Visit YouTube Channel",
      icon: <i className="fab fa-youtube"></i>,
      url: "https://www.youtube.com/@schoolofintology4062",
      onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
    },
    {
      label: "Join Discord",
      icon: <i className="fab fa-discord"></i>,
      url: "https://discord.gg/DYkESHwgVA",
      onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
    },
  ];

const loggedInOptions = [
  {
    label: "Submit Document",
    icon: <i className="fas fa-paper-plane"></i>, // Optional: add an icon
    onClick: handleSubmit, // This will trigger your submit logic
  },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
  {
    label: "Your Documents",
    onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Walkthroughs",
    onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Notebook",
    onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Your Workspace",
    onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
  },
  {
    label: "Account Settings",
    onClick: () => window.open(`/account/${uid}`, "_blank", "noopener,noreferrer"),
  },
];

  // Use loggedInOptions if signed in, otherwise loggedOutOptions
  const menuOptions = uid ? loggedInOptions : loggedOutOptions;
  const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  // Add this after the ReactQuill component
  useEffect(() => {
    if (quillRef.current) {
      // Get the toolbar element
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      
      // Find the undo and redo buttons and replace their content with Font Awesome icons
      const undoButton = toolbar.container.querySelector('.ql-undo');
      const redoButton = toolbar.container.querySelector('.ql-redo');
      
      if (undoButton) {
        undoButton.innerHTML = '<i class="fas fa-undo"></i>';
        undoButton.title = 'Undo';
      }
      
      if (redoButton) {
        redoButton.innerHTML = '<i class="fas fa-redo"></i>';
        redoButton.title = 'Redo';
      }
      decorateInsertTemplateButton(toolbar);
    }
  }, [quillRef.current]);

  useEffect(() => {
    const handleResize = () => {
      setShowSplitView(window.innerWidth > 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  });

  // Theme cleanup on unmount

  // Theme Configuration Effect


  useEffect(() => {
    if (!selectedViewDoc) return;
    setLoadingMessage('Fetching side view document...');
    const fetchViewDocument = async () => {
      try {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;
        if (!token) {
          console.error("User is not authenticated.");
          return;
        }
        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/page/${selectedViewDocName}/${uid}/`,
          {
            method: "GET",
            headers: {
              "requesting-user-uid": uid,
              "requesting-user-email": userEmail,
              "auth-token": `Bearer ${token}`,
              "Authorization": `${token}`,
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setViewDocContent(transformContent(data.content));
          setViewDocTitle(data.page_name);
        } else {
          const response2 = await fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/documentation/space/${selectedViewDocName}/${uid}/`,
            {
              method: "GET",
              headers: {
                "requesting-user-uid": uid,
                "requesting-user-email": userEmail,
                "auth-token": `Bearer ${token}`,
                "Authorization": `${token}`,
              },
            }
          );
          if (response2.ok) {
            const data2 = await response2.json();
            setViewDocContent(transformContent(data2.content));
            setViewDocTitle(data2.name);
          }
        }
      } catch (error) {
        console.error("Error fetching document: ", error);
      }
    };
    fetchViewDocument();
  }, [selectedViewDoc]);

  // User Data Fetching Effect
  useEffect(() => {
    if (uid) {
      setLoadingMessage('Fetching user account details...');
      fetchPageDetails();
      fetchAccountDetails();
    }
  }, [uid]);

  // Documentation Population Effect
  useEffect(() => {
    if (uid) {
      setLoadingMessage('Loading documentation data...');
      populateDocumentation();
    }
  }, [uid]);

  const fetchAccountDetails = async () => {
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/users/${uid}/`,
        {
          headers: {
            "Authorization": `${token}`
          }
        }
      );
      const data = await response.json();

      if (response.ok) {
        setYoutubeLink(data.youtube_channel);
        setCustomLink(data.custom_domain);
        setCustomLinkTitle(data.custom_domain_title);
        setDiscordLink(data.discord_server);
        setLinkedInLink(data.linkedin_link);
        setGithubLink(data.github_link);
        setTwitterLink(data.twitter_link);
        setBlueskyLink(data.bluesky_link);
      }
    } catch (error) {
      console.error("Error in fetching account details: ", error);
    }
  };

  const populateDocumentation = async () => {
    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const headers = token ? { 'Authorization': `${token}` } : {};
      const [spaceResponse, pageResponse] = await Promise.all([
        fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/spaces/author/" +
            uid +
            "/",
          { headers }
        ),
        fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/pages/author/" +
            uid +
            "/",
          { headers }
        ),
      ]);

      const spaceData =
        spaceResponse.status === 200
          ? await spaceResponse.json()
          : { spaces: [] };
      const pageData =
        pageResponse.status === 200 || pageResponse.status === 404
          ? await pageResponse.json()
          : [];
      const spaceOptions = Array.isArray(spaceData.spaces)
        ? spaceData.spaces.map((space) => ({
            label: space.name,
            value: space.id,
          }))
        : [];
      const pageOptions = Array.isArray(pageData.pages)
        ? pageData.pages.map((page) => ({
            label: page.page_name,
            value: page.id,
          }))
        : [];
      setAllDocumentation([...spaceOptions, ...pageOptions]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchPageDetails = async () => {
    try {
      if (uid.length !== 0) {
        const user = auth.currentUser;
        const token = user ? await user.getIdToken() : null;
        const response = await fetch(
          "https://bluebirddocumentationadmin.pythonanywhere.com/page-details/author/" +
            uid +
            "/",
          {
            method: "GET",
            headers: { 
                "Content-Type": "application/json",
                ...(token && { 'Authorization': `${token}` })
            },
          }
        );

        if (response.status === 200) {
          setHasSpaces(true);
        } else if (response.status === 404) {
          setHasSpaces(false);
        } else {
          console.log("Failed to fetch page detail data.");
        }
      }
    } catch (error) {
      console.error("Error getting data from page detail table:", error);
    }
  };

  const maxLengths = {
    Space: 100,
    Page: 150,
  };

  const currentMaxLength = maxLengths[selectedType] || 100;

  const handleToggle = (e) => {
    setIsPublic(e.target.checked);
  };
  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
  };
  const handleSelectedType = (e) => {
    setSelectedType(e.target.value);
  };

  const handleAddEmail = () => {
    if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
      setEmails([...emails, { email: currentEmail, permission: "Can Edit" }]);
      setCurrentEmail("");
    } else {
      alert("This email has already been added.");
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
  };

  const handlePermissionChange = (index, newPermission) => {
    const updatedEmails = [...emails];
    updatedEmails[index].permission = newPermission;
    setEmails(updatedEmails);
  };  

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [FONT_TOOLBAR_ENTRY],
          [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
          [{ size: ["small", false, "large"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["inline-code"],
          ["formula"],
          ["table-better"], // table button
          ["clean"],
          ["image"],
          ["insert-template"], // Insert a snippet template (BBD-108)
          ["undo", "redo"], // Add undo and redo buttons
        ],
        handlers: {
          undo: function() {
            this.quill.history.undo();
          },
          redo: function() {
            this.quill.history.redo();
          },
          "insert-template": insertTemplateToolbarHandler
        }
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
        tableCreationAttributes: {
          className: 'responsive-table',
          style: 'width: 100%; max-width: 100%;'
        }
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );

  const createPermissionTableEntry = async () => {
    const data = {
      name: name,
      documentationType: selectedType,
      userEmail: emails,
      isPublic: isPublic,
      documentUid: uid,
      documentUrl: `documentation/${selectedType.toLowerCase()}/${name}/${uid}/`,
      description: description,
    };

    try {
      const user = auth.currentUser;
      const token = user ? await user.getIdToken() : null;
      const response = await fetch(
        "https://bluebirddocumentationadmin.pythonanywhere.com/permissions/create/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { 'Authorization': `${token}` })
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || "Failed to create permissions.");
      }

      // BBD-73: a 201 can still be partial — addresses on either side of a
      // block are skipped and reported back in blocked_emails.
      notifyBlockedShareRecipients(await readShareResponse(response));
      
    } catch (error) {
      console.error("Error setting permissions:", error);
      throw error; // Propagate the error to be caught by the parent function
    }
  };

  const transformContent = (htmlContent) => {
    if (!htmlContent) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");

    // Demote headings two levels for display (h1→h3 … h4→h6) so all four
    // editor levels stay distinct while h1/h2 remain reserved for page
    // chrome. Deepest first so freshly created tags aren't re-demoted.
    [["h4", "h6"], ["h3", "h5"], ["h2", "h4"], ["h1", "h3"]].forEach(([from, to]) => {
      doc.querySelectorAll(from).forEach((heading) => {
        const demoted = document.createElement(to);
        demoted.innerHTML = heading.innerHTML;
        heading.replaceWith(demoted);
      });
    });

    doc.querySelectorAll("img").forEach((img) => {
      img.style.maxWidth = "100%";
      img.style.height = "auto";
      img.style.objectFit = "contain";
    });

    return doc.body.innerHTML;
  };

  const formatExtractedText = (text) => {
    // Detect paragraphs by newlines or specific patterns
    const paragraphs = text.split(/\n(?=[A-Z])/).map((paragraph) => {
      // Replace single newlines with <br /> for line breaks inside paragraphs
      return `<p>${paragraph.trim().replace(/\n/g, "<br />")}</p>`;
    });

    return paragraphs.join("");
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    setIsLoading(true);
    setLoadingMessage('Processing uploaded file...');

    try {
      let rawText = "";
      if (file.type === "application/pdf") {
        setLoadingMessage('Extracting text from PDF...');
        rawText = await extractTextFromPDF(file);
      } else if (file.type.startsWith("image/")) {
        setLoadingMessage('Extracting text from image...');
        rawText = await extractTextFromImage(file);
      } else {
        alert("Unsupported file type. Please upload an image or PDF.");
        setIsLoading(false);
        return;
      }

      setLoadingMessage('Formatting extracted text...');
      // Format the extracted text
      const formattedText = formatExtractedText(rawText);

      // Set the formatted content into the editor
      setContent((prevContent) => `${prevContent}${formattedText}`);
      setIsLoading(false);
    } catch (error) {
      console.error("Error processing file:", error);
      setIsLoading(false);
    }
  };

  const extractTextFromImage = async (file) => {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = async () => {
        try {
          const {
            data: { text },
          } = await Tesseract.recognize(reader.result, "eng", {
            logger: (info) => console.log(info), // Monitor progress
          });
          resolve(text);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const extractTextFromPDF = async (file) => {
    const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
    let fullText = "";
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map((item) => item.str).join(" ");
      fullText += `\nPage ${i}:\n${pageText}`;
    }
    return fullText;
  };

    const handleAttachmentUpload = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;

    // List of allowed extensions
    const allowedExtensions = ['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4'];

    // Get the file extension including the dot
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

    // Check if the file extension is in the allowed list
    if (!allowedExtensions.includes(fileExtension)) {
        alert('Only ZIP, PDF, JPEG, PNG, MP3, and MP4 files are allowed as attachments');
        event.target.value = ''; // Clear the input
        return;
    }

    // Validate file size (8MB limit)
    const maxSize = 8 * 1024 * 1024; // 8MB in bytes
    if (file.size > maxSize) {
        alert('File size exceeds the maximum limit of 8MB');
        event.target.value = ''; // Clear the input
        return;
    }

    setAttachmentFile(file);
    };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentDescription('');
    // Clear the file input
    const fileInput = document.getElementById('attachment-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleHomeButtonClick = () => {
    navigate("/");
  };
  const handleLoginButtonClick = () => {
    navigate("/login");
  };
  const handleRegisterButtonClick = () => {
    navigate("/register");
  };
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleManualSnapshot = async () => {
    setSnapshotSaveStatus('saving');
    try {
      const success = await saveManualSnapshot();
      setSnapshotSaveStatus(success ? 'success' : 'error');
      setTimeout(() => setSnapshotSaveStatus(null), 3000); // Clear status after 3 seconds
    } catch (error) {
      console.error('Error saving manual snapshot:', error);
      setSnapshotSaveStatus('error');
      setTimeout(() => setSnapshotSaveStatus(null), 3000);
    }
  };

  if (!uid) {
    return (
      <div 
        className="DocumentationMainPage__div"
      onContextMenu={
        enhancedRightClickEnabled ? handleContextMenu : undefined
      }
    >
        {/* <Sidebar /> */}
        <div className="DocumentationMainPage__main-content">
          <p className="large">It looks like you're not signed in.</p>
          <p className="medium">
            <strong>
              To create a document, you must have an account with us
            </strong>
            . If you already have an account, you can login by clicking the
            Login button below. If you don't already have an account, you can
            create a new one by clicking the Register button below.
          </p>
          <button
            onClick={handleHomeButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Home
          </button>
          <button
            onClick={handleLoginButtonClick}
            style={{ marginRight: "10px" }}
            className="ConflictResolver__button"
          >
            Login
          </button>
          <button
            onClick={handleRegisterButtonClick}
            className="ConflictResolver__button"
          >
            Register
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

    if (isLoading) {
            return (
                <>
                    <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                        <div className="coffee-cup-loader">
                            <div className="cup">
                                <div className="cup-body"></div>
                                <div className="cup-handle"></div>
                                <div className="cup-base"></div>
                                <div className="cup-shadow"></div>
                                <div className="steam steam1"></div>
                                <div className="steam steam2"></div>
                                <div className="steam steam3"></div>
                            </div>
                        </div>
                        <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
                    </div>
                </>
            )
    }

  return (
    <div
        className="Create__div DocEditor"
      onContextMenu={
        enhancedRightClickEnabled ? handleContextMenu : undefined
      }
    >
      <Sidebar />

      <div className="Create__main-content">
        <h2>Create a Space/Page</h2>
        <div className="TplStart__row">
          <button type="button" className="TplStart__btn" onClick={templatePrefill.openGallery} disabled={templatePrefill.busy}>
            <i className="fas fa-puzzle-piece"></i>&nbsp; Start from a template
          </button>
          {templatePrefill.error && <span className="TplStart__error">{templatePrefill.error}</span>}
        </div>
        <div className="Create__required-fields-div">
        <div className="Create__form-group Create__required-field-margin-top">
          <select
            className="form-select Create__input Create__required-field-select"
            aria-label="Default select example"
            name="selected_type"
            value={selectedType}
            onChange={handleSelectedType}
            required
          >
            <option value="">- Select Content Type -</option>
            <option value="Space">Space</option>
            <option value="Page" disabled={!hasSpaces}>
              Page
            </option>
          </select>
        </div>
        {selectedType === "Page" && (
          <div className="Create__form-group">
            <label style={{ paddingTop: "3px" }} className="Create__required-field">
              Select a parent space or page:{" "}
            </label>
            <Select
              options={allDocumentation}
              value={allDocumentation.find(
                (option) => option.value === selectedParent
              )}
              onChange={(option) =>
                setSelectedParent(option ? option.label : "")
              }
              placeholder="Select a Space"
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        )}
        <div className="Create__form-group">
          <label className="Create__required-field">Name: </label>
          <input
            type="text"
            id="name"
            className="Create__input-short-field Create__required-field-select"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={currentMaxLength}
            placeholder={`Max ${currentMaxLength} characters`}
            required
          />
        </div>

        {/* Duplicate Detection Alert */}
        {showDuplicatesSuggestions && duplicateSuggestions.length > 0 && (
          <div className="Create__duplicate-suggestions">
            <div className="Create__duplicate-header">
              <i className="fas fa-exclamation-triangle Create__duplicate-icon"></i>
              <strong>Similar Documents Found ({duplicateSuggestions.length})</strong>
            </div>
            <p className="Create__duplicate-subtitle">
              We found documents with similar titles or descriptions. Consider reviewing them before creating a new one (click to view):
            </p>
            {duplicateSuggestions.map((duplicate, index) => {
              const documentUrl = `/documentation/${duplicate.document_type.toLowerCase()}/${duplicate.name}/${duplicate.author_uid}/`;
              return (
                <div 
                  key={index} 
                  className="Create__duplicate-item"
                  onClick={() => window.open(documentUrl, "_blank")}
                  style={{ cursor: "pointer" }}
                  title="Click to view this document in a new tab"
                >
                  <div className="Create__duplicate-item-header">
                    <h4>{duplicate.name}</h4>
                    <span className="Create__similarity-badge">
                      {Math.round(duplicate.similarity_score * 100)}% match
                    </span>
                  </div>
                  {duplicate.description && (
                    <p className="Create__duplicate-description">{duplicate.description}</p>
                  )}
                  <div className="Create__duplicate-meta">
                    <div className="Create__duplicate-meta-item">
                      <span className="Create__meta-label">Type:</span>
                      <span className="Create__meta-value">{duplicate.document_type}</span>
                    </div>
                    <div className="Create__duplicate-meta-item">
                      <span className="Create__meta-label">Author:</span>
                      <span className="Create__meta-value">{duplicate.author_name || duplicate.author_email}</span>
                    </div>
                    {duplicate.space_name && (
                      <div className="Create__duplicate-meta-item">
                        <span className="Create__meta-label">Space:</span>
                        <span className="Create__meta-value">{duplicate.space_name}</span>
                      </div>
                    )}
                    <div className="Create__duplicate-meta-item">
                      <span className="Create__meta-label">Updated:</span>
                      <span className="Create__meta-value">
                        {new Date(duplicate.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="Create__duplicate-scores">
                    <div className="Create__score-bar">
                      <span className="Create__score-label">Title Similarity: {Math.round(duplicate.title_similarity * 100)}%</span>
                      <div className="Create__score-fill" style={{ width: `${duplicate.title_similarity * 100}%` }}></div>
                    </div>
                    {duplicate.description_similarity !== null && (
                      <div className="Create__score-bar">
                        <span className="Create__score-label">Description Similarity: {Math.round(duplicate.description_similarity * 100)}%</span>
                        <div className="Create__score-fill" style={{ width: `${duplicate.description_similarity * 100}%` }}></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </div>
        <div className="Create__form-group">
          <label>Short Description: </label>
          <input
            type="text"
            id="description"
            className="Create__input-full-field"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            This description is what invited users will see about this
            space/page.
          </p>
        </div>
        <div className="Create__form-group">
          <label>Make this content public?</label>
          <label className="Create__switch">
            <input type="checkbox" checked={isPublic} onChange={handleToggle} />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Public content is viewable by anyone that has this document's URL.
          </p>
        </div>
        <div className="Create__form-group">
          <label>Publish this page upon submitting?</label>
          <label className="Create__switch">
            <input
              type="checkbox"
              checked={completed}
              onChange={handleCompletedToggle}
            />
            <span className="Create__slider round"></span>
          </label>
        </div>
        <div className="Create__form-group-helper-text">
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            Whether the document is public or not, this document will not be{" "}
            <strong>viewable</strong> by anyone; including added collaborators
            that have "Can View" permission. Collaborators that have "Can Edit"
            permission can edit this document.
          </p>
        </div>
        {selectedType === "Space" && (
          <>
            <div className="Create__form-group">
              <label>Show a table of contents on this space?</label>
              <label className="Create__switch">
                <input
                  type="checkbox"
                  checked={showTableOfContents}
                  onChange={(e) => setShowTableOfContents(e.target.checked)}
                />
                <span className="Create__slider round"></span>
              </label>
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                When enabled, viewers see an "In this space" section near the
                top of the space listing the pages inside it that they have
                access to.
              </p>
            </div>
          </>
        )}
        <div
          className="Create__form-group"
          style={{ flexDirection: "column", marginBottom: "60px" }}
        >
          <label>Invite others to collaborate on this content.</label>
          {emails.map((item, index) => (
            <div
              key={index}
              className="Create__email-item"
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              <span>{item.email}</span>
              <select
                value={item.permission}
                className="Create__select"
                onChange={(e) => handlePermissionChange(index, e.target.value)}
                style={{ marginLeft: "10px", padding: "5px" }}
              >
                <option value="Can Edit">Can Edit</option>
                <option value="Can View">Can View</option>
              </select>
              <button
                onClick={() => handleRemoveEmail(index)}
                style={{
                  marginLeft: "10px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <i
                  className="fas fa-trash-alt"
                  title="Remove Email"
                  style={{ color: "white" }}
                ></i>
              </button>
            </div>
          ))}
          <div className="Create__email-input-container">
            <input
              type="email"
              className="Create__email-input"
              placeholder="Enter E-mail Address"
              value={currentEmail}
              onChange={(e) => setCurrentEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
            />
            <button onClick={handleAddEmail} className="Create__add-email-btn">
              <i className="fas fa-plus"></i>
            </button>
          </div>
          <div className="Create__form-group-helper-text">
            <i
              className="fas fa-info-circle Create__info-icon"
              title="Information"
            ></i>
            <p className="Create__helper-text">
              As of this current version, any added permissions will{" "}
              <strong>not</strong> automatically be added to any future child
              documents that are created.
            </p>
          </div>
        </div>

        <div className="Create__form-group" style={{ marginBottom: "20px" }}>
          <label>Attach File (Optional):</label>
          <input
            id="attachment-input"
            type="file"
            className="Create__file-upload"
            accept=".zip,.pdf,.jpeg,.png,.mp3,.mp4"
            onChange={handleAttachmentUpload}
            style={{ marginBottom: "10px" }}
          />
          <button
            type="button"
            onClick={() => setCcPickerOpen(true)}
            style={{ display: 'block', marginBottom: '10px', marginRight: '12px', background: 'none', border: '1px solid #0d6efd', color: '#0d6efd', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
          >
            <i className="fas fa-box-open"></i> Choose from Command Center
          </button>
          {attachmentFile && (
            <div className="Create__attachment-info">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                <button
                  type="button"
                  onClick={removeAttachment}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#dc3545",
                    fontSize: "16px"
                  }}
                  title="Remove attachment"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          )}
        </div>
        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            You can attach a ZIP, PDF, JPEG, PNG, MP3, or MP4 file containing additional resources, source code, or documentation files. 
            Maximum file size is 8MB.
          </p>
        </div>

        <input
          type="text"
          placeholder="Custom Link Title"
          value={customLinkTitle}
          onChange={(e) => setCustomLinkTitle(e.target.value)}
          className="Account__input-custom-title"
        />
        <input
          id="customLinkInput"
          type="text"
          placeholder="Custom Link Domain"
          value={customLink}
          onChange={(e) => setCustomLink(e.target.value)}
          className="Account__edit-input"
        />

        <div className="Account__link-divs">
          <i class="fa-brands fa-discord Account__icons"></i>
          <input
            type="text"
            value={discordLink}
            placeholder="Discord Server Link"
            onChange={(e) => setDiscordLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-square-x-twitter Account__icons"></i>
          <input
            type="text"
            value={twitterLink}
            placeholder="X Profile Link"
            onChange={(e) => setTwitterLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-bluesky Account__icons"></i>
          <input
            type="text"
            value={blueskyLink}
            placeholder="Bluesky Profile Link"
            onChange={(e) => setBlueskyLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-linkedin Account__icons"></i>
          <input
            type="text"
            value={linkedInLink}
            placeholder="LinkedIn Profile Link"
            onChange={(e) => setLinkedInLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-youtube Account__icons"></i>
          <input
            type="text"
            value={youtubeLink}
            placeholder="YouTube Channel Link"
            onChange={(e) => setYoutubeLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div className="Account__link-divs">
          <i class="fa-brands fa-square-github Account__icons"></i>
          <input
            type="text"
            value={githubLink}
            placeholder="GitHub Profile Link"
            onChange={(e) => setGithubLink(e.target.value)}
            className="Account__edit-input"
          />
        </div>

        <div
          className="Create__form-group-helper-text"
          style={{ marginBottom: "60px" }}
        >
          <i
            className="fas fa-info-circle Create__info-icon"
            title="Information"
          ></i>
          <p className="Create__helper-text">
            These are links that users will see when visiting this document. The
            links are automatically pulled from what's saved in your account
            settings. Feel free to edit any of the links if you don't want to
            use what's pulled from your account settings.{" "}
            <strong>
              If you don't want your saved link(s) to show for this particular
              document,{" "}
            </strong>
            just remove the contents of that input; until you see the
            placeholder text again.
          </p>
        </div>

        {showSplitView && (
          <div className="EditDocument__split-view-selector">
            <label>Select Side View Document:</label>
            <Select
              options={allDocumentation}
              value={allDocumentation.find(
                (doc) => doc.label === selectedViewDocName
              )}
              onChange={(option) => {
                setSelectedViewDoc(option ? option.value : null);
                setSelectedViewDocName(option ? option.label : "");
              }}
              placeholder="Choose a document..."
              isSearchable={true}
            />
          </div>
        )}

        <div className="EditDocument__edit-document-container">
          <div
            className={`EditDocument__editor-section ${
              selectedViewDoc ? "split" : ""
            }`}
          >
            <div
              className={`EditDocument__quill-container ${
                isFullscreen ? "fullscreen-mode" : ""
              }`}
            >
              <div className="EditDocument__quill-toolbar"></div>
              <div className="QuillOverrides__content-formatting">
              <ReactQuill
                ref={quillRef}
                value={content}
                onChange={setContent}
                modules={modules}
                style={{ height: isFullscreen ? "100vh" : "70vh" }}
              />
              <TaskReferenceDropdown {...taskRefProps} />
              </div>
              <button
                onClick={toggleFullscreen}
                className="EditDocument__fullscreen-btn EditDocument__fullscreen-btn-corner"
                title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                style={{ bottom: isFullscreen ? "20px" : "-18px" }}
              >
                <i
                  className={`fas ${
                    isFullscreen ? "fa-compress-alt" : "fa-expand-alt"
                  }`}
                ></i>
              </button>
            </div>
          </div>

          {selectedViewDoc && showSplitView && (
            <div className="EditDocument__view-document-section">
              <div className="EditDocument__view-document-header">
                <p className="medium">
                  <strong>Viewing:</strong> <i>{viewDocTitle}</i>
                </p>
                <button
                  onClick={() => setSelectedViewDoc(null)}
                  className="EditDocument__close-btn"
                >
                  ✖
                </button>
              </div>
              <div className="EditDocument__view-document-content">
                <div dangerouslySetInnerHTML={{ __html: viewDocContent }} />
              </div>
            </div>
          )}
        </div>
        <div className="EditDocument__action-buttons">
          <button onClick={handleSubmit} className="submit-button">
            Submit
          </button>
        </div>
      </div>
      {enhancedRightClickEnabled && ContextMenuComponent}
      {templatePrefill.element}
      {templateSnippets.element}
      <CommandCenterFilePickerModal
        isOpen={ccPickerOpen}
        onClose={() => setCcPickerOpen(false)}
        onSelect={(file) => setAttachmentFile(file)}
        allowedExtensions={['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4']}
        maxSizeBytes={8 * 1024 * 1024}
      />
    </div>
  );
}

export default Create;
